import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import { HowItWorksCTA } from '@/reports/shared/ReportSection/components';
import { AutomobileRepairs } from '@/reports/types/vehicles';
import { Stack, Text } from '@/theme/index';
import { VehicleServiceRepairsCard } from './VehicleServiceRepairsCard/VehicleServiceRepairsCard';

interface VehicleServiceRepairsProps {
  repairs: AutomobileRepairs; // A list of repairs to be displayed.
}

function HowItWorksCTAContent() {
  return (
    <Stack
      direction="column"
      fontSize="0.9rem"
      spacing={2}
      color={(theme) => theme.palette.grey[800]}
    >
      <Text variant="body2">
        This section provides information about the vehicle's maintenance,
        repairs, and compliance with safety and emissions standards. Service
        records show routine maintenance, while repair records highlight
        significant repairs made to the vehicle. Inspection records indicate the
        vehicle's adherence to safety and emissions regulations. Examining this
        section helps evaluate the vehicle's maintenance history, major issues,
        and compliance with standards. However, data availability may vary.
      </Text>
      <Text variant="body2">
        Please note that the information provided in this section is based on
        data obtained from a limited number of sources and does not provide
        nationwide coverage. It is important to understand that the records
        included in this report may not encompass services, repairs and
        inspections conducted in all states or jurisdictions.
      </Text>
    </Stack>
  );
}

/**
 * This component displays a section of the report dedicated to vehicle service repairs.
 * It lists all the repairs that have been reported.
 *
 * @component
 * @param {Object} props - Props for the component.
 * @param {AutomobileRepairs} props.repairs - An array of repair data to be displayed.
 *
 * @returns {JSX.Element} Rendered VehicleServiceRepairs component.
 */
export function VehicleServiceRepairs({
  repairs = [],
}: VehicleServiceRepairsProps) {
  return (
    <ReportSection
      showTopBar
      title="Service and Repairs"
      hasContent={repairs.length > 0}
      sectionId="vehicle-service-and-repairs"
      howItWorks={<HowItWorksCTA content={HowItWorksCTAContent()} />}
      customEmptyText={
        <EmptyVehicleSectionDisclaimer title="service and repairs" />
      }
    >
      {repairs.map((repair, index) => {
        return (
          <VehicleServiceRepairsCard key={`repair-${index}`} repair={repair} />
        );
      })}
    </ReportSection>
  );
}
